import styled from '@emotion/styled';
import { sdsColor, sdsRadius } from '@staging-design-system/tokens';
import { rem } from '@utils';

const Root = styled.div<{ $showLabel?: boolean }>(
  ({ $showLabel = true }) => `
  position: relative;
  width: ${$showLabel ? '100%' : 'auto'};
`
);

const Trigger = styled.button<{ $showLabel?: boolean }>(
  ({ $showLabel = true }) => `
  align-items: center;
  background: ${sdsColor.background.defaultSecondary};
  border: none;
  border-radius: ${sdsRadius.xSmall};
  color: ${sdsColor.text.defaultDefault};
  cursor: pointer;
  display: flex;
  gap: ${rem(8)};
  font-family: inherit;
  font-size: ${rem(14)};
  font-weight: 400;
  justify-content: ${$showLabel ? 'flex-start' : 'center'};
  line-height: ${rem(20)};
  min-height: ${$showLabel ? rem(40) : 'auto'};
  padding: ${rem(8)};
  text-align: left;
  transition:
    background 200ms ease,
    color 200ms ease;
  width: ${$showLabel ? '100%' : 'auto'};

  &:hover:not(:disabled) {
    background: ${sdsColor.background.defaultDefaultHover};
  }

  &:active:not(:disabled) {
    background: ${sdsColor.background.defaultDefaultHover};
  }

  &:focus-visible {
    outline: ${rem(2)} solid ${sdsColor.border.brandSecondary};
    outline-offset: -${rem(2)};
  }

  &:disabled {
    background: transparent;
    color: ${sdsColor.text.disabledDefault};
    cursor: not-allowed;
  }
`
);

const LeadingIcon = styled.span`
  align-items: center;
  color: inherit;
  display: inline-flex;
  flex-shrink: 0;
  height: ${rem(16)};
  justify-content: center;
  width: ${rem(16)};

  svg {
    display: block;
    height: ${rem(16)};
    width: ${rem(16)};
  }
`;

const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrailingIcon = styled.span<{ $expanded?: boolean }>(
  ({ $expanded }) => `
  align-items: center;
  color: inherit;
  display: inline-flex;
  flex-shrink: 0;
  height: ${rem(16)};
  justify-content: center;
  width: ${rem(16)};

  svg {
    display: block;
    height: ${rem(8)};
    transition: transform 200ms ease;
    transform: rotate(${$expanded ? '180deg' : '0deg'});
    width: ${rem(8)};
  }
`
);

const Menu = styled.ul<{ $placement?: 'bottom' | 'top' }>(
  ({ $placement = 'bottom' }) => `
  background: ${sdsColor.background.defaultDefault};
  border-radius: ${sdsRadius.xSmall};
  box-shadow: 0 ${rem(4)} ${rem(4)} 0 ${sdsColor.background.defaultTertiary};
  display: flex;
  flex-direction: column;
  left: 0;
  list-style: none;
  overflow: clip;
  padding: 0;
  position: absolute;
  transition: 200ms ease;
  min-width: 100%;
  width: max-content;
  z-index: 10;

  ${
    $placement === 'top'
      ? `
    bottom: 100%;
    margin: 0 0 ${rem(8)};
    top: auto;
  `
      : `
    margin: ${rem(8)} 0 0;
    top: 100%;
  `
  }
`
);

const MenuItem = styled.li`
  display: flex;
  width: 100%;
`;

const MenuItemButton = styled.button`
  align-items: center;
  background: ${sdsColor.background.defaultDefault};
  border: none;
  color: ${sdsColor.text.defaultDefault};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  gap: ${rem(8)};
  height: ${rem(48)};
  padding: 0 ${rem(16)};
  text-align: left;
  transition: background 200ms ease;
  width: 100%;

  &:hover:not(:disabled) {
    background: ${sdsColor.background.defaultDefaultHover};
  }

  &:focus-visible {
    outline: ${rem(2)} solid ${sdsColor.border.brandSecondary};
    outline-offset: -${rem(2)};
  }

  &:disabled {
    color: ${sdsColor.text.disabledOnDisabled};
    cursor: not-allowed;
  }
`;

const MenuItemIcon = styled.span`
  align-items: center;
  color: inherit;
  display: inline-flex;
  flex-shrink: 0;
  height: ${rem(16)};
  justify-content: center;
  width: ${rem(16)};

  svg {
    display: block;
    height: ${rem(16)};
    width: ${rem(16)};
  }
`;

const MenuItemText = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;
`;

const MenuItemLabel = styled.span`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  white-space: nowrap;
`;

const MenuItemDescription = styled.span`
  color: ${sdsColor.text.defaultSecondary};
  font-size: ${rem(12)};
  font-weight: 400;
  line-height: ${rem(16)};

  button:disabled & {
    color: inherit;
  }
`;

export default {
  Label,
  LeadingIcon,
  Menu,
  MenuItem,
  MenuItemButton,
  MenuItemDescription,
  MenuItemIcon,
  MenuItemLabel,
  MenuItemText,
  Root,
  TrailingIcon,
  Trigger
};
